import { FEED7_BOOKS } from '../lib/feed7';
import { RU_BOOKS, isRuBook } from './mirrorDesk';
import { CupisPairs } from './CupisPairs';
import { CupisIndicator, CupisDiagnostics } from './CupisPanel';
import { Decimal } from 'decimal.js';
import { POLYQUANT_BOOKS } from '../lib/polyquant';
import { FEED7, EXPERIMENTS, POLYQUANT, PRIVATE_MIRROR, FEED_KIND, RU_FEED, TRANSFERS } from '../lib/feedContext';
import './transfer-filters.css';
import { transferFloor } from '../lib/transferFeed';
/**
 * Сито: быстрые фильтры ленты, свёрнутые по умолчанию.
 *
 * Свёрнутые — это главное свойство, а не экономия места. Сканер, встречающий
 * человека двадцатью полями, теряет его раньше, чем покажет первую вилку;
 * умолчание остаётся одной ручкой «беру от ₽», и всё остальное открывается по
 * желанию. Но опытному вилочнику фильтр по конторам нужен на первой минуте: у
 * него порезаны лимиты в одних, нет счёта в других, а третьи он не берёт
 * принципиально — и чужие строки он читает глазами на каждом кадре.
 *
 * ЧТО ЗДЕСЬ ЕСТЬ И ЧЕГО НЕТ. Тонкая настройка живёт в студии фильтров: там
 * проценты, возраст, правила, «мои конторы», сохранённые профили и стол
 * (открытие касс). Здесь — только то, что человек меняет посреди работы за
 * одну секунду, не отводя глаз от ленты: порог, лайв или прематч, конторы,
 * дисциплина, рынок. Всё прочее из сита убрано (12.08, владелец; стол —
 * 26.08): поля процента и возраста дублировали студию, а стол в куче с
 * фильтрами вилки учили крутить не то. Вторая копия настройки — это два
 * разных ответа на один вопрос. Там же убрана кнопка «мягкие» («бестолковый
 * режим»): она выдавала одну частную границу процента за способ работы, а
 * нажатая по ошибке опустошала верх ленты. Границы, поставленные студией,
 * при этом видны в свёрнутой строке отдельной фишкой и снимаются нажатием:
 * невидимый фильтр опаснее лишнего поля.
 *
 * ОТКУДА БЕРУТСЯ ВАРИАНТЫ. Из того, что показывала лента ЭТОГО контура
 * (`./seen`), а не из общего каталога площадок: полсотни кнопок с чужими
 * букмекерами, которых наш движок не отдаёт, — это работа по отсеиванию,
 * переложенная на человека. Код, увиденный хоть раз, остаётся в списке
 * надолго, порядок алфавитный, а ноль вилок сейчас — подпись, а не запрет:
 * «умная» сортировка по числу переставляла бы кнопки под курсором на каждом
 * кадре (08.08, тестер). Выбранное показывается всегда, даже если в этом
 * контуре такой конторы ещё не видели: фильтр, который режет ленту и не виден,
 * читается как пустой рынок.
 *
 * КАК ЭТО ЛЕЖИТ. Двумя дорожками, а не шестью полосами во всю ширину. До 12.08
 * каждая настройка была строкой «подпись слева в колонке 92 пикселя — фишки
 * следом — пустота до правого края», и панель читалась как штабель тёмных полос
 * (владелец: «длинные чёрные полосы, просто ужас»). Хуже всех выглядел
 * переключатель «только эти / все, кроме»: он стоял в колонке под подписью и
 * растягивался колонкой на всю ширину, превращаясь в чёрную планку на тысячу
 * пикселей с двумя словами у левого края. Главная — отдельная полка над счетами,
 * не третье чтение тех же фишек. Теперь подпись стоит НАД содержимым,
 * блок получает ширину по содержимому, слева идёт то, что задают числом, справа
 * то, что выбирают нажатием, а конторы занимают отдельную полосу — им она нужна.
 *
 * Свёрнутое сито обязано называть, что на нём стоит. Фильтр, о котором забыли,
 * выглядит как пустая лента, и это самый дорогой вид тишины: человек решит, что
 * рынок спокоен, и уйдёт.
 */

import { useEffect, useMemo, useState } from 'react';

import { MIRROR_FEED } from '../lib/build';
import { UNNAMED_VENUE } from '../lib/protocol';
import { SIEVE_SPORTS, SPAN_CHIP, SPORT_GROUPS, sieveSportCodes, sportCanon, sportGroup, sportIconOf, sportLabel, uniqSportChips } from '../lib/sports';
import { bookOf, isRubBook, isSourceCode, sportOf } from './mirrorDesk';
import { useSettings } from '../store/settings';
import { useAlarm } from './alarm';
import { Books, isMirrorBook, CUPIS_BOOKS } from './Books';
import { rub } from './format';
import { gateFloor, gateText, okGate, stepGate } from './gate';
import {
  ENTRY_SIEVE,
  type Filters,
  SORT_NAMES,
  type SortKey,
  sportTitle,
  useFilters,
} from './filters';
import { MARKET_GROUPS, MARKET_NAMES, marketTags } from '../lib/markets';
import { BookEditor } from './BookEditor';
import { type Meta, sportName, venueName } from './meta';
import { ChipList, type Option, Seg } from './Pick';
import { applyProfile, dropProfile, type Profile, useProfile } from './profile';
import { isAllFeed, pageLine, studioHref } from './route';
import { remember } from './seen';
import { TAPE_IDS, TAPE_NAMES, isFeedTape, tapeOfFork, tapesForRoute, routeTapes, tapeAllowed } from './tapes';
import { sieveDropsBook, venueCanon } from './venues';
import { SHOW_NAMES, SHOW_UNITS, useShow } from './show';
import { useScreen } from './store';
import { useFeedBand } from './useBand';
import { fromFilters, same, useWatch, watchText } from './watch';

/**
 * Деньги и порог — внутри сита.
 *
 * Раньше это была отдельная панель над лентой: банк, курс, «беру от» и
 * измеренный темп в один ряд. Владелец (08.08): «беру от 100» и «а что мельче
 * 50 — в хвост» — это же чистые фильтры, им место туда; банк и курс в ленте
 * ничем не помогают. Верно и то и другое: банк с курсом задают однажды, порог
 * решает состав ленты, а лента должна начинаться с вилок.
 *
 * Порог стоит первым и крупнее соседей: его крутят каждый день, а банк с курсом
 * ставят однажды и потом только сверяются. Свёрнутое сито при этом обязано
 * называть банк и порог одной строкой: суммы в ленте считаются из них, и число
 * без видимого источника — это цифра, которой нечем возразить.
 */
/** Деньги в валюте профита: при $ и % — доллары по курсу, иначе рубли. */
function moneyText(rubles: number, usd: boolean, fx: number): string {
  if (!usd) return `${rub(rubles)} ₽`;
  const value = new Decimal(rubles).div(fx);
  return value.abs().lt(100) ? `$${value.toDecimalPlaces(2).toString()}` : `$${rub(value.round().toNumber())}`;
}

function Money(): React.ReactElement {
  const gate = useSettings((s) => s.gate);
  const setGate = useSettings((s) => s.setGate);
  const bank = useSettings((s) => s.bank);
  const setBank = useSettings((s) => s.setBank);
  const fx = useSettings((s) => s.fx);
  const setFx = useSettings((s) => s.setFx);
  const polyWallet = useSettings((s) => s.polyWallet);
  const setPolyWallet = useSettings((s) => s.setPolyWallet);
  const fxStale = useSettings((s) => s.fxStale);
  const fxServer = useSettings((s) => s.fxServer);
  const meta = useScreen((s) => s.meta);
  const unit = useShow((s) => s.unit);
  const setUnit = useShow((s) => s.setUnit);
  const ageMin = useFilters((s) => s.ageMin);
  const setAgeMin = useFilters((s) => s.setAgeMin);
  const usd = unit !== 'rub' && fx > 0;
  const own = fxServer !== null && Math.abs(fx - fxServer) > 0.005;

  return (
    <div className="nx-cell nx-money">
      {/* Та же подпись, что на кнопке свёрнутого сита: «беру от 300 ₽». Два
          названия одной ручки человек читает как две разные настройки. */}
      <u>беру от</u>
      <div className="nx-money__gate">
        <b>{gateText(gate, unit, bank, fx)}</b>
        <s>
          <button
            type="button"
            aria-label="ниже порог"
            disabled={gate <= gateFloor(bank)}
            onClick={() => setGate(stepGate(gate, false, unit, bank, fx))}
          >
            −
          </button>
          <button type="button" aria-label="выше порог" onClick={() => setGate(stepGate(gate, true, unit, bank, fx))}>
            +
          </button>
        </s>
      </div>
      {/* Следствие ручки — её же словами. Два числа порога читались как два
          разных правила, пока между ними не было сказано «а что мельче». */}
      <i className="nx-money__why">
        {RU_FEED ? 'вилки от −4 %; все строки показаны полностью'
          : `что мельче ${gateText(okGate(gate), unit, bank, fx)}, уедет вниз списка, в ленте останется`}
      </i>
      {/* Банк, курс и темп — справочные числа: их ставят однажды и потом только
          сверяются. До 12.08 они стояли одним рядом с порогом и уезжали к
          правому краю панели, за тысячу пикселей от него; глаз выбирал из
          четырёх одинаковых чисел то, которое крутят каждый день. Крутят ровно
          одно, и оно теперь стоит выше и крупнее, а эти три — столбиком под ним. */}
      <div className="nx-money__refs">
        <label
          className="nx-money__c"
          title="сколько денег вы кладёте в одну вилку: из этой суммы считаются ставки на обе ноги"
        >
          <span>сумма</span>
          <input
            type="number"
            min={usd ? 1 : 100}
            step={usd ? 10 : 1000}
            value={usd ? new Decimal(bank).div(fx).toDecimalPlaces(0).toNumber() : bank}
            aria-label={usd ? 'сумма в долларах' : 'сумма в рублях'}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (!Number.isFinite(raw)) return;
              setBank(usd ? new Decimal(raw).times(fx).round().toNumber() : raw);
            }}
          />
          <u>{usd ? '$' : '₽'}</u>
        </label>
        <label
          className="nx-money__c"
          title={
            own
              ? `по этому курсу считаются ноги в долларах. здесь стоит ваше число, у цб сейчас ${fxServer?.toFixed(2) ?? '—'}`
              : 'по этому курсу считаются ноги в долларах. поставьте своё, если покупаете валюту дороже'
          }
        >
          <span>курс</span>
          <input
            type="number"
            min={1}
            step={0.1}
            value={fx}
            aria-label="курс доллара"
            onChange={(e) => setFx(Number(e.target.value))}
          />
          {/* Протухший курс называется протухшим. Считать по нему можно, но
              человек обязан знать, что вторая нога пересчитана вчерашним рублём. */}
          <u className={`nx-money__fresh ${fxStale && !own ? 'warn' : ''}`}>
            {own
              ? `ваш, у цб ${fxServer?.toFixed(2) ?? '—'}`
              : fxStale
                ? 'цб не обновил'
                : `цб ${meta.fx.asOf?.slice(11, 16) ?? ''}`}
          </u>
        </label>
        <label
          className="nx-money__c"
          title="адрес из профиля Polymarket: карточка увидит, что шары пришли. хранится только в этом браузере"
        >
          <span>кошелёк Polymarket</span>
          <input
            type="text"
            spellCheck={false}
            placeholder="0x…"
            value={polyWallet}
            aria-label="кошелёк Polymarket"
            onChange={(e) => setPolyWallet(e.target.value)}
          />
        </label>
        <label className="nx-money__c" title="лайв-вилки моложе этого числа секунд скрыты: только что родившаяся вилка часто мигалка. пусто — выключено">
          <span>свежесть</span>
          <input
            type="number"
            min={0}
            max={900}
            step={1}
            placeholder="выкл"
            value={ageMin ?? ''}
            aria-label="свежесть от, секунд"
            onChange={(e) => {
              const raw = Number(e.target.value);
              setAgeMin(e.target.value === '' || !Number.isFinite(raw) || raw <= 0 ? null : Math.round(raw));
            }}
          />
          <u>с</u>
        </label>
        <span className="nx-money__c">
          <span>профит</span>
          <span className="nx-money__seg" role="group" aria-label="профит в ленте">
            {SHOW_UNITS.map((code) => (
              <button
                key={code}
                type="button"
                className={unit === code ? 'on' : ''}
                aria-pressed={unit === code}
                onClick={() => setUnit(code)}
              >
                {SHOW_NAMES[code]}
              </button>
            ))}
          </span>
        </span>
      </div>
    </div>
  );
}

/** Стоимость — потеря: 1% платы = NET −1%. Границы в ROI не меняем.
 * «От» — плюсовая потеря (сколько готовы заплатить), «до» — минус (вилка в плюс). */
function signedCost(value: number): string {
  const body = String(Math.abs(value)).replace('.', ',');
  if (value < 0) return `−${body}`;
  if (value > 0) return `+${body}`;
  return body;
}

function TransferCost(): React.ReactElement {
  const floor = transferFloor(useFilters((s) => s.roiMin));
  const ceiling = useFilters((s) => s.roiMax) ?? 1;
  const setRoi = useFilters((s) => s.setRoi);
  const budget = useSettings((s) => s.transferBudget);
  const setBudget = useSettings((s) => s.setTransferBudget);
  const pmCap = useSettings((s) => s.caps.polymarket);
  const pmCur = useSettings((s) => s.capsCur.polymarket ?? 'USD');
  const setCap = useSettings((s) => s.setCap);
  const minimum = new Decimal(ceiling).negated().toNumber();
  const maximum = new Decimal(floor).negated().toNumber();
  const change = (raw: string, lower: boolean): void => {
    if (raw.trim() === '') return;
    try {
      const parsed = new Decimal(raw.replace(',', '.'));
      if (!parsed.isFinite()) return;
      const cost = Decimal.max(-1, Decimal.min(5, parsed)).toDecimalPlaces(2);
      if (lower) setRoi(floor, Decimal.min(cost, maximum).negated().toNumber());
      else setRoi(Decimal.max(cost, minimum).negated().toNumber(), ceiling);
    } catch {
      // Незавершённый ввод не меняет действующий фильтр.
    }
  };
  return (
    <div className="nx-cell nx-money">
      <u>стоимость попытки</u>
      <div className="nx-money__gate">
        <b>{`${signedCost(maximum)}…${signedCost(minimum)} %`}</b>
      </div>
      <i className="nx-money__why">
        {`от ${signedCost(maximum)} % до ${signedCost(minimum)} % · плюс — платите за перелив, минус — вилка в плюс`}
      </i>
      <div className="nx-money__refs">
        <label className="nx-money__c">
          <span>от</span>
          <input type="number" min={minimum} max={5} step="0.1" value={maximum}
            aria-label="стоимость от, потеря %"
            onChange={(e) => change(e.target.value, false)} />
          <u>%</u>
        </label>
        <label className="nx-money__c">
          <span>до</span>
          <input type="number" min={-1} max={maximum} step="0.1" value={minimum}
            aria-label="стоимость до, плюс %"
            onChange={(e) => change(e.target.value, true)} />
          <u>%</u>
        </label>
        <label className="nx-money__c" title="остаток на БК: из него считается нога, не встречная ставка">
          <span>на БК</span>
          <input type="number" inputMode="decimal" min="0.01" step="0.01"
            aria-label="сумма на балансе БК" value={budget.amount}
            onChange={(e) => setBudget({ ...budget, amount: e.target.value })} />
          <select aria-label="валюта баланса БК" value={budget.currency}
            onChange={(e) => setBudget({ ...budget, currency: e.target.value === 'RUB' ? 'RUB' : 'USD' })}>
            <option value="USD">$</option>
            <option value="RUB">₽</option>
          </select>
        </label>
        <label className="nx-money__c" title="остаток на Polymarket: режет встречную ногу. То же поле, что баланс в студии">
          <span>на PM</span>
          <input type="number" inputMode="decimal" min="0" step="0.01"
            aria-label="сумма на балансе Polymarket" value={pmCap ?? ''}
            onChange={(e) => {
              const raw = e.target.value.trim().replace(',', '.');
              if (raw === '') {
                setCap('polymarket', null);
                return;
              }
              const parsed = Number(raw);
              setCap('polymarket', Number.isFinite(parsed) ? parsed : null, pmCur);
            }} />
          <select aria-label="валюта баланса Polymarket" value={pmCur}
            onChange={(e) => setCap('polymarket', pmCap ?? null, e.target.value === 'RUB' ? 'RUB' : 'USD')}>
            <option value="USD">$</option>
            <option value="RUB">₽</option>
          </select>
        </label>
      </div>
    </div>
  );
}

const byName = (a: Option, b: Option): number => a.name.localeCompare(b.name, 'ru');

/** Режимы — закрытый список из двух; показываем оба всегда, даже ночью. */
const LINES = ['live', 'prematch'] as const;

const LINE_NAMES: Record<string, string> = { live: 'лайв', prematch: 'прематч' };

/**
 * Из чего выбирать: конторы — что эта лента показывала; дисциплины и рынки —
 * закрытый пул, не текущий кадр. Иначе баскетбол и «4 карта» нельзя выключить,
 * пока в ленте их нет. Число рядом — из кадра; ноль ничего не запрещает.
 *
 * Выбранное добавляется к списку всегда. Иначе фишка, поставленная вчера или
 * приехавшая профилем, режет ленту невидимо — а невидимый фильтр человек ищет
 * не в сите, а в наших поломках.
 */
function useOptions(chosen: {
  venues: readonly string[];
  sports: readonly string[];
  markets: readonly string[];
  tapes: readonly string[];
}): {
  venues: Option[];
  sports: Option[];
  markets: Map<string, number>;
  lines: Option[];
  tapes: Option[];
  masked: number;
} {
  const forks = useScreen((s) => s.forks);
  const meta = useScreen((s) => s.meta);
  const pickedVenues = chosen.venues.filter((one) => one !== '').join(',');
  const pickedSports = chosen.sports.join(',');
  const pickedTapes = chosen.tapes.filter(isFeedTape).join(',');
  const pathname = location.pathname;
  return useMemo(() => {
    const venues = new Map<string, number>();
    const sports = new Map<string, { name: string; count: number }>();
    const markets = new Map<string, number>();
    const lines = new Map<string, number>();
    const tapes = new Map<string, number>();
    const locked = pageLine(pathname);
    const wantTapes = pickedTapes === '' ? [] : pickedTapes.split(',');
    for (const fork of forks.values()) {
      if (locked !== null) {
        const line = typeof fork.line === 'string' ? fork.line : null;
        if (line !== null && line !== locked) continue;
      }
      const tape = tapeOfFork(fork);
      tapes.set(tape, (tapes.get(tape) ?? 0) + 1);
      if (wantTapes.length > 0 && !wantTapes.includes(tape)) continue;
      for (const leg of fork.legs ?? []) {
        if (leg.s === UNNAMED_VENUE) {
          venues.set(UNNAMED_VENUE, (venues.get(UNNAMED_VENUE) ?? 0) + 1);
          continue;
        }
        const book = bookOf(leg.s);
        if (book !== '') venues.set(book, (venues.get(book) ?? 0) + 1);
      }
      const sport = sportOf(fork);
      if (sport !== '') {
        const seen = sports.get(sport);
        sports.set(sport, {
          name: seen?.name ?? (MIRROR_FEED ? sportLabel(sport) : sportTitle(fork)),
          count: (seen?.count ?? 0) + 1,
        });
      }
      for (const tag of marketTags(fork)) markets.set(tag, (markets.get(tag) ?? 0) + 1);
      const line = typeof fork.line === 'string' ? fork.line : null;
      if (line !== null) lines.set(line, (lines.get(line) ?? 0) + 1);
    }
    const all = (known: readonly string[], picked: string): string[] =>
      [...new Set([...known, ...picked.split(',').filter((one) => one !== '')])];
    const keptVenues = new Set(pickedVenues.split(',').filter((one) => one !== ''));
    const sportCodes = sieveSportCodes(
      SIEVE_SPORTS,
      remember('sports', sports.keys()),
      pickedSports.split(','),
    );
    return {
      // До оплаты сервер отдаёт все ноги под одним псевдонимом, и он приезжает
      // сюда как площадка с именем `hidden`. Фильтровать по нему нечего: он
      // означает не контору, а «имя скрыто», и кнопкой «только hidden» человек
      // выключил бы всю ленту. Убираем из списка, но не из выбранного: чужую
      // фишку, поставленную раньше, снимать за человека нельзя.
      venues: all(
        RU_FEED ? [...RU_BOOKS, ...POLYQUANT_BOOKS] : TRANSFERS && MIRROR_FEED
          ? meta.transferBooks
          : [
              ...(FEED7 ? FEED7_BOOKS : POLYQUANT ? Object.keys(meta.venues) : PRIVATE_MIRROR ? [...CUPIS_BOOKS] : remember('books', venues.keys())),
              ...meta.sources.map((source) => venueCanon(source.code)).filter(Boolean),
            ],
        pickedVenues,
      )
        .filter((code) => !FEED7 || FEED7_BOOKS.includes(code))
        .filter((code) => !isSourceCode(code))
        .filter((code) => code !== UNNAMED_VENUE || keptVenues.has(code))
        .filter((code) => !TRANSFERS || (code !== 'polymarket' && code !== UNNAMED_VENUE && !meta.venues[code]?.exchange
          && !['kalshi', 'predictfun', 'sxbet', 'betfair', 'orbitex'].includes(code)))
        .filter((code) => !RU_FEED || isRuBook(code) || POLYQUANT_BOOKS.includes(code))
        .filter((code) => !MIRROR_FEED || RU_FEED || PRIVATE_MIRROR || FEED7 || POLYQUANT || !isRubBook(code))
        .filter((code) => !EXPERIMENTS || isMirrorBook(code))
        .filter((code) => PRIVATE_MIRROR || FEED7 || POLYQUANT || (FEED_KIND === 'main' || RU_FEED) && POLYQUANT_BOOKS.includes(code) || !sieveDropsBook(code))
        .map((code) => ({ code, name: venueName(meta, code), count: venues.get(code) ?? 0 }))
        .sort(byName),
      masked: venues.get(UNNAMED_VENUE) ?? 0,
      sports: uniqSportChips(
        sportCodes.filter(code => !PRIVATE_MIRROR || sportCanon(code) !== 'table-tennis').map((code) => ({
          code,
          name: sportName(meta, code),
          count: sports.get(code)?.count ?? 0,
        })),
      )
        .sort((a, b) => {
          return byName(a, b);
        }),
      // Рынки — весь справочник (`MARKET_GROUPS`), не кадр: здесь только счёт меток.
      markets,
      lines: LINES.map((code) => ({
        code,
        name: LINE_NAMES[code] ?? code,
        count: lines.get(code) ?? 0,
      })),
      tapes: TAPE_IDS.map((code) => ({
        code,
        name: TAPE_NAMES[code],
        count: tapes.get(code) ?? 0,
      })),
    };
  }, [forks, meta, pickedVenues, pickedSports, pickedTapes, pathname]);
}

/**
 * Один вопрос сита: подпись сверху, ответ под ней.
 *
 * Подпись стоит НАД содержимым, а не слева от него. Слева она была до 12.08, и
 * из-за неё каждая настройка занимала полосу во всю ширину панели: колонка
 * подписей в 92 пикселя, фишки следом, а дальше на полтора экрана пустота.
 * Владелец: «длинные чёрные полосы, просто ужас». Подпись сверху делает из
 * настройки блок шириной по её содержимому, а блоки уже складываются в колонки.
 */
function Cell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="nx-cell">
      <u>{label}</u>
      {children}
    </div>
  );
}

/** Дисциплины колонками, как конторы: заголовок отмечает группу целиком. */
function SportShelves({
  options,
  chosen,
  onToggle,
  onSet,
}: {
  options: readonly Option[];
  chosen: readonly string[];
  onToggle: (code: string) => void;
  onSet: (codes: string[]) => void;
}): React.ReactElement {
  const groups = [...SPORT_GROUPS, { id: 'other', name: 'прочее', codes: [] as readonly string[] }]
    .map((group) => ({ ...group, items: options.filter((one) => sportGroup(one.code) === group.id) }))
    .filter((group) => group.items.length > 0);
  return (
    <div className="nx-sports">
      {groups.map((group) => {
        const picked = group.items.filter((one) => chosen.includes(sportCanon(one.code))).length;
        const live = group.items.reduce((sum, one) => sum + one.count, 0);
        const all = picked === group.items.length;
        return (
          <section className="nx-sports__sh" key={group.id}>
            <button
              type="button"
              className={`nx-sports__t${picked > 0 ? ' on' : ''}`}
              title={all ? 'снять всю группу' : 'отметить всю группу'}
              onClick={() => {
                const codes = group.items.map((one) => sportCanon(one.code));
                onSet(all ? chosen.filter((code) => !codes.includes(code)) : [...new Set([...chosen, ...codes])]);
              }}
            >
              <b>{group.name}</b>
              {picked > 0 ? <i>{picked} из {group.items.length}</i> : live > 0 ? <em>{live} в ленте</em> : null}
            </button>
            {group.items.map((one) => {
              const on = chosen.includes(sportCanon(one.code));
              return (
                <button
                  key={one.code}
                  type="button"
                  className={`nx-sp ${on ? 'on' : ''} ${one.count === 0 && !on ? 'zero' : ''}`}
                  aria-pressed={on}
                  onClick={() => onToggle(one.code)}
                >
                  {sportIconOf(one.code) === null ? (
                    <span className="nx-bk__i" aria-hidden="true" />
                  ) : (
                    <img className="nx-bk__i" src={sportIconOf(one.code)!} alt="" loading="lazy" decoding="async" />
                  )}
                  <span className="nx-bk__n">{one.name}</span>
                  {one.count > 0 && <i>{one.count}</i>}
                </button>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Подписка на своё сито в Telegram.
 *
 * Стоит внутри фильтров намеренно: подписываются на то, что видно на экране, а
 * не на второй список контор в отдельном разделе — см. шапку `watch.ts`.
 *
 * Три состояния, и все три названы словами: не подписан, подписан на то же
 * самое, подписан на другое. Третье важнее прочих: человек поправил фишки, а
 * телефон продолжает звать его на старое сито, и молчание об этом выглядит как
 * поломка оповещений.
 */
export function Watch({ f }: { f: Filters }): React.ReactElement | null {
  const canSend = useWatch((s) => s.canSend);
  const watch = useWatch((s) => s.watch);
  const busy = useWatch((s) => s.busy);
  const save = useWatch((s) => s.save);
  const drop = useWatch((s) => s.drop);
  const load = useWatch((s) => s.load);
  useEffect(() => void load(), [load]);

  const now = useMemo(() => fromFilters(f, ''), [f]);
  if (canSend !== true) return null;
  const stale = watch !== null && !same(watch, now);

  return (
    <div className="nx-sieve__row nx-sieve__watch">
      <u>в telegram</u>
      {watch === null ? (
        <>
          <button type="button" disabled={busy} onClick={() => void save(fromFilters(f, watchText(now)))}>
            слать это сито
          </button>
          <span className="nx-sieve__note">
            бот пришлёт только то, что проходит нынешние фильтры: {watchText(now)}
          </span>
        </>
      ) : (
        <>
          <span className="nx-sieve__note">
            подписка: <b>{watchText(watch)}</b>
            {stale && <i>, фильтры с тех пор изменились</i>}
          </span>
          {stale && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save(fromFilters(f, watchText(now)))}
            >
              подписать на нынешние
            </button>
          )}
          <button type="button" disabled={busy} onClick={() => void drop()}>
            отписаться
          </button>
        </>
      )}
    </div>
  );
}

/** Одна снимаемая нажатием подпись о том, что стоит на сите. */
interface Tag {
  key: string;
  text: string;
  drop: () => void;
}

const decimal = (value: number): string => String(value).replace('.', ',');
const costRange = (f: Filters): string =>
  `${signedCost(new Decimal(transferFloor(f.roiMin)).negated().toNumber())}…${signedCost(new Decimal(f.roiMax ?? 1).negated().toNumber())}`;

/** Смысл отметок в шапке контор. Угадывать его по пустой ленте нельзя. */
function venueHint(f: Filters): string {
  if (TRANSFERS) {
    if (f.venues.length === 0) return 'отметьте БК, с которых переливаете';
    if (f.venueMode === 'only') return 'в ленте останутся пары отмеченных БК с Polymarket';
    return 'из ленты уйдут пары отмеченных БК';
  }
  if (f.venues.length === 0) return 'отметьте, где у вас есть счёт';
  if (f.venueMode === 'only') return 'в ленте останутся вилки, где отмечены обе конторы';
  return 'из ленты уйдут вилки, где есть хоть одна отмеченная';
}

function marketHint(f: Filters): string {
  if (f.markets.length === 0) return 'отметьте рынки, которые не берёте или только которые берёте';
  if ((f.marketMode ?? 'except') === 'only') return 'останутся вилки с отмеченным типом ставки и отрезком';
  return 'из ленты уйдут вилки с любой отмеченной меткой';
}

function mainHint(f: Filters): string {
  if (f.mainVenues.length === 0) return 'нажмите контору — она обязана стоять на ноге; «только эти / все, кроме» работают вместе';
  if (f.mainVenues.length === 1) return 'в ленте останутся вилки, где эта контора на ноге';
  return 'в ленте останутся вилки, где есть хотя бы одна из отмеченных';
}

/** «4 конторы», «5 контор». Число с неверным окончанием читается как опечатка. */
const plural = (n: number, one: string, few: string, many: string): string => {
  const hundred = n % 100;
  const ten = n % 10;
  if (hundred >= 11 && hundred <= 14) return many;
  if (ten === 1) return one;
  if (ten >= 2 && ten <= 4) return few;
  return many;
};

/**
 * Что стоит на сите — словами и по одному.
 *
 * Раньше это была склеенная строка «только 3 контор · 2 дисц. · мягкие»: она
 * называла фильтр, но снять его можно было только целиком, кнопкой «снять всё».
 * Человек, который поставил тотал и лайв, а хочет вернуть прематч, вынужден был
 * открыть сито и найти нужную фишку среди прочих. Каждая подпись поэтому —
 * кнопка, снимающая ровно себя.
 *
 * Пока фильтров немного, они названы поимённо («Пиннакл», «теннис»): имя
 * отвечает на вопрос «что именно скрыто» без открывания сита. Когда их больше
 * трёх, имена сворачиваются в счёт — иначе строка заголовка становится вторым
 * списком контор.
 */
function tagsOf(f: Filters, meta: Meta, profile: Profile | null): Tag[] {
  const tags: Tag[] = [];
  const many = 3;

  if (f.mainVenues.length > 0) {
    if (f.mainVenues.length <= many) {
      for (const code of f.mainVenues) {
        tags.push({
          key: `main:${code}`,
          text: `главная ${venueName(meta, code)}`,
          drop: () => useFilters.getState().toggleMainVenue(code),
        });
      }
    } else {
      const n = f.mainVenues.length;
      tags.push({
        key: 'mains',
        text: `главная ${n} ${plural(n, 'контора', 'конторы', 'контор')}`,
        drop: () => useFilters.getState().apply({ mainVenues: [] }),
      });
    }
  }

  if (f.venues.length > 0) {
    const mode = f.venueMode === 'only' ? 'только' : 'кроме';
    if (f.venues.length <= many) {
      for (const code of f.venues) {
        tags.push({
          key: `venue:${code}`,
          text: `${mode} ${venueName(meta, code)}`,
          drop: () => useFilters.getState().toggleVenue(code),
        });
      }
    } else {
      const n = f.venues.length;
      tags.push({
        key: 'venues',
        text:
          f.venueMode === 'only'
            ? `только ${n} ${plural(n, 'контора', 'конторы', 'контор')}`
            : `кроме ${n} ${plural(n, 'конторы', 'контор', 'контор')}`,
        drop: () => useFilters.getState().apply({ venues: [] }),
      });
    }
  }

  if (f.sports.length > 0) {
    if (f.sports.length <= many) {
      for (const code of f.sports) {
        tags.push({
          key: `sport:${code}`,
          text: sportName(meta, code),
          drop: () => useFilters.getState().toggleSport(code),
        });
      }
    } else {
      const n = f.sports.length;
      tags.push({
        key: 'sports',
        text: `${n} ${plural(n, 'дисциплина', 'дисциплины', 'дисциплин')}`,
        drop: () => useFilters.getState().apply({ sports: [] }),
      });
    }
  }

  if (f.markets.length > many) {
    const n = f.markets.length;
    tags.push({
      key: 'markets',
      text: `${n} ${plural(n, 'рынок', 'рынка', 'рынков')}`,
      drop: () => useFilters.getState().apply({ markets: [] }),
    });
  } else {
    for (const code of f.markets) {
      tags.push({
        key: `market:${code}`,
        text: `${(f.marketMode ?? 'except') === 'only' ? 'только' : 'кроме'} ${MARKET_NAMES[code] ?? code}`,
        drop: () => useFilters.getState().toggleMarket(code),
      });
    }
  }

  for (const code of f.lines) {
    if (pageLine(location.pathname) !== null || isAllFeed(location.pathname)) break;
    tags.push({
      key: `line:${code}`,
      text: LINE_NAMES[code] ?? code,
      drop: () => useFilters.getState().toggleLine(code),
    });
  }

  for (const code of f.tapes) {
    if (FEED_KIND === 'main' || FEED_KIND === 'tests') break;
    if (!isFeedTape(code)) continue;
    tags.push({
      key: `tape:${code}`,
      text: TAPE_NAMES[code],
      drop: () => useFilters.getState().toggleTape(code),
    });
  }

  // В CUPIS границы редактируются здесь, в остальных лентах — в студии. Снять
  // их отсюда обязано быть можно: полосу приносит забытый профиль, а верх ленты
  // она опустошает молча.
  if (f.roiMin !== null || f.roiMax !== null) {
    tags.push({
      key: 'roi',
      text: TRANSFERS ? `стоимость ${costRange(f)} %`
        : `процент ${f.roiMin === null ? '…' : decimal(f.roiMin)}–${f.roiMax === null ? '…' : decimal(f.roiMax)} %`,
      drop: () => useFilters.getState().setRoi(null, null),
    });
  }
  if (PRIVATE_MIRROR && f.cupisFlow === 'transfers') {
    tags.push({ key: 'cupis-flow', text: 'переливы', drop: () => useFilters.getState().apply({ cupisFlow: 'arbs', roiMin: null, roiMax: null }) });
  }
  if (PRIVATE_MIRROR && f.cupisPairMode && f.cupisPairMode !== 'all') {
    const text = f.cupisPairMode === 'russian' ? 'БК между собой'
      : f.cupisPairMode === 'poly' ? 'БК + биржи' : `выбрано пар: ${f.cupisPairs?.length ?? 0}`;
    tags.push({ key: 'cupis-pairs', text, drop: () => useFilters.getState().apply({ cupisPairMode: 'all', cupisPairs: [] }) });
  }
  if (f.ageMax !== null && pageLine(location.pathname) !== 'prematch') {
    tags.push({
      key: 'age',
      text: `не старше ${f.ageMax} с`,
      drop: () => useFilters.getState().setAgeMax(null),
    });
  }
  if (f.ageMin !== null && pageLine(location.pathname) !== 'prematch') {
    tags.push({
      key: 'fresh',
      text: `свежесть от ${f.ageMin} с`,
      drop: () => useFilters.getState().setAgeMin(null),
    });
  }
  if (ENTRY_SIEVE && f.entryOnly) {
    tags.push({
      key: 'entry',
      text: 'только вход',
      drop: () => useFilters.getState().setEntryOnly(false),
    });
  }
  if (RU_FEED && profile !== null && profile.prec !== 'any') {
    tags.push({
      key: 'prec',
      text: profile.prec === 'coupon' ? 'купон' : profile.prec === 'match' ? 'матч' : 'сайт',
      drop: () => applyProfile({ ...profile, prec: 'any' }),
    });
  }

  if (TRANSFERS && f.transferFull) {
    tags.push({ key: 'transfer-full', text: 'полностью исполнимые', drop: () => useFilters.getState().setTransfer('transferFull', false) });
  }
  if (TRANSFERS && f.transferRules) {
    tags.push({ key: 'transfer-rules', text: 'правила проверены', drop: () => useFilters.getState().setTransfer('transferRules', false) });
  }
  if (TRANSFERS && f.transferBook) {
    tags.push({ key: 'transfer-book', text: 'хватает PM-стакана', drop: () => useFilters.getState().setTransfer('transferBook', false) });
  }

  if (profile !== null && profile.skipLiveStretch) {
    tags.push({
      key: 'stretch',
      text: 'без идущего отрезка',
      drop: () => applyProfile({ ...profile, skipLiveStretch: false }),
    });
  }
  if (profile !== null) {
    for (const span of profile.offSpans) {
      tags.push({
        key: `span:${span}`,
        text: `без ${SPAN_CHIP[span] ?? span}`,
        drop: () =>
          applyProfile({
            ...profile,
            offSpans: profile.offSpans.filter((one) => one !== span),
          }),
      });
    }
  }

  return tags;
}

export function Sieve({
  hidden,
  byProfile = 0,
  byExclude = 0,
}: {
  hidden: number;
  byProfile?: number;
  byExclude?: number;
}): React.ReactElement {
  const f = useFilters();
  const alarm = useAlarm();
  const options = useOptions({
    venues: [...f.venues, ...f.mainVenues],
    sports: f.sports,
    markets: f.markets,
    tapes: tapesForRoute(f.tapes, location.search),
  });
  const profile = useProfile((s) => s.profile);
  const meta = useScreen((s) => s.meta);
  const gate = useSettings((s) => s.gate);
  const bank = useSettings((s) => s.bank);
  const transferBudget = useSettings((s) => s.transferBudget);
  const fx = useSettings((s) => s.fx);
  const show = useShow((s) => s.unit);
  const [markMain, setMarkMain] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const shelf = useScreen((s) => s.shelf);
  const setShelf = useScreen((s) => s.setShelf);
  const hiddenForks = useScreen((s) => s.hiddenForks);
  const scope = routeTapes(location.pathname, location.search);
  const hiddenN = [...hiddenForks.values()].filter(one => tapeAllowed(one.fork, scope)).length;
  const locked = pageLine(location.pathname);
  const tags =
    shelf === 'hidden'
      ? [
          { key: 'shelf', text: 'скрытые', drop: () => setShelf('work') },
          ...tagsOf(f, meta, profile),
        ]
      : tagsOf(f, meta, profile);
  const line = f.lines.length === 1 ? (f.lines[0] as 'live' | 'prematch') : 'all';
  const lane = shelf === 'hidden' ? 'hidden' : line;
  const band = useFeedBand();
  const fist = band === 'fist';

  const sourceBooks = (
    <div className="nx-cell nx-cell--wide">
      <div className="nx-cell__h">
        <u>конторы</u>
        <div className="nx-cell__mode">
          <div className="nx-pick" role="group" aria-label="как читать отметки">
            {([
              ['only', 'только эти'],
              ['except', 'все, кроме'],
            ] as const).map(([code, name]) => (
              <button
                key={code}
                type="button"
                className={!markMain && f.venueMode === code ? 'on' : ''}
                aria-pressed={!markMain && f.venueMode === code}
                onClick={() => {
                  setMarkMain(false);
                  f.setVenueMode(code);
                }}
              >
                {name}
              </button>
            ))}
            {!TRANSFERS && (
              <button
                type="button"
                className={markMain ? 'on' : ''}
                aria-pressed={markMain}
                title="отметьте контору, которая обязана стоять на ноге; «только эти» и «все, кроме» работают вместе с ней"
                onClick={() => setMarkMain(!markMain)}
              >
                главная
                {f.mainVenues.length > 0 && <i>{f.mainVenues.length}</i>}
              </button>
            )}
          </div>
          <i>{markMain ? mainHint(f) : venueHint(f)}</i>
        </div>
      </div>
      <Books
        kind={markMain ? 'main' : 'accounts'}
        meta={meta}
        options={options.venues}
        chosen={markMain ? f.mainVenues : f.venues}
        mains={f.mainVenues}
        onToggle={markMain ? f.toggleMainVenue : f.toggleVenue}
        onSet={(codes) => f.apply(markMain ? { mainVenues: codes } : { venues: codes })}
        onEdit={TRANSFERS ? undefined : setEditing}
      />
      {/* До оплаты имён у ног нет вовсе, и пустая полка без слов читалась
          бы как «мы не знаем ни одной конторы». */}
      {options.masked > 0 && (
        <p className="nx-sieve__note">
          часть ног сейчас без имени: {options.masked} в кадре. имена контор откроются после
          оплаты, и отметить их можно будет там же.
        </p>
      )}
      {editing !== null && (
        <BookEditor code={editing} name={venueName(meta, editing)} onClose={() => setEditing(null)} />
      )}
    </div>
  );

  return (
    <div className={`nx-sieve ${f.open ? 'open' : ''}${TRANSFERS ? ' nx-sieve--transfers' : ''}`}>
      <div className="nx-sieve__head">
        {/* Вход в фильтры — кнопка, а не сноска. Мелким серым текстом он весил
            меньше переключателей порядка справа, хотя за ним стоит весь состав
            ленты, а за ними — только её очерёдность (08.08, владелец). Счётчик
            на кнопке отвечает на «стоит ли что-нибудь» до того, как её нажмут. */}
        <button
          type="button"
          className={`nx-sieve__toggle${f.open ? ' on' : ''}`}
          aria-expanded={f.open}
          onClick={() => f.setOpen(!f.open)}
        >
          <span className="nx-sieve__caret" aria-hidden="true">
            {f.open ? '−' : '+'}
          </span>
          фильтры
          {tags.length > 0 && <i>{tags.length}</i>}
        </button>
        {/* Порог и банк названы даже при свёрнутом сите: из них считается
            каждая сумма ниже, и молчать — то же самое, что молчать о фильтре.
            Стоят одним голосом (13.08, владелец): банк из шапки сюда, кегль и
            цвет как у «беру от». Нажатие открывает сито — там их и правят. */}
        {!f.open && (
          <>
            <button
              type="button"
              className="nx-sieve__money"
              title="порог правится внутри фильтров"
              onClick={() => f.setOpen(true)}
            >
              {TRANSFERS ? 'стоимость ' : 'беру от '}<b>{TRANSFERS ? `${costRange(f)} %` : gateText(gate, show, bank, fx)}</b>
            </button>
            <button
              type="button"
              className="nx-sieve__money"
              title="сумма правится внутри фильтров"
              onClick={() => f.setOpen(true)}
            >
              {TRANSFERS ? 'на БК ' : 'сумма '}<b>{TRANSFERS
                ? `${transferBudget.amount || '—'} ${transferBudget.currency === 'USD' ? '$' : '₽'}`
                : moneyText(bank, show !== 'rub' && fx > 0, fx)}</b>
            </button>
          </>
        )}
        {ENTRY_SIEVE && <button
          type="button"
          className={`nx-sieve__entry${f.entryOnly ? ' on' : ''}`}
          aria-pressed={f.entryOnly}
          title="только строки, где хотя бы одна нога ведёт на матч или исход. секционная ссылка при жизни вилки — не товар"
          onClick={() => f.setEntryOnly(!f.entryOnly)}
        >
          только вход
        </button>}
        {/* Свёрнутое сито называет, что на нём стоит, и снимает это по одной
            штуке. Забытый фильтр выглядит как спокойный рынок, и это самая
            дорогая тишина в продукте. */}
        {tags.length > 0 && (
          <div className="nx-sieve__tags">
            {tags.map((tag) => (
              <button
                key={tag.key}
                type="button"
                className="nx-tag"
                title={`снять: ${tag.text}`}
                onClick={tag.drop}
              >
                {tag.text}
                <i aria-hidden="true">×</i>
              </button>
            ))}
          </div>
        )}
        {hidden > 0 && shelf !== 'hidden' && (
          <span className="nx-sieve__hid">
            скрыто <b>{hidden}</b>
            {/* Профиль назван отдельно от фишек. «Скрыто 40» при пустых фишках
                выглядит ошибкой сканера, пока не сказано, что режет профиль,
                применённый в студии и с тех пор забытый.
                Фразу собираем целиком, а не склеиваем из кусков: «скрыто 98
                всё — профилем "работа"» читалось обрывком (08.08, владелец). */}
            {byProfile > 0 && profile !== null && (
              <>
                {' '}
                <i>
                  {byProfile === hidden
                    ? `все профилем «${profile.name}»`
                    : `из них ${byProfile} профилем «${profile.name}»`}
                </i>
              </>
            )}
            {byExclude > 0 && (
              <>
                {' '}
                <i>
                  {byExclude === hidden
                    ? 'все — исключённый матч у кассы'
                    : `из них ${byExclude} — исключённый матч у кассы`}
                </i>
              </>
            )}
          </span>
        )}
        {(MIRROR_FEED || tags.length > 1 || byProfile > 0) && (
          <button
            type="button"
            className="nx-sieve__clear"
            onClick={() => {
              f.clear();
              setShelf('work');
              if (MIRROR_FEED || byProfile > 0) dropProfile();
            }}
          >
            {MIRROR_FEED ? 'сбросить все фильтры' : 'снять всё'}
          </button>
        )}
        {PRIVATE_MIRROR && <CupisIndicator />}
        <span className="nx-sieve__gap" />
        {/* Выключатель звука обязан быть БЫСТРЫМ и на виду: человек тянется к
            нему в момент, когда сигнал мешает, а не когда есть время искать
            его в настройках. Значки вместо слов (07.08, владелец): динамик и
            колокольчик читаются без чтения, а перечёркнутый вариант сам
            говорит «выключено». Слова остаются в title и для читалок. */}
        <div className="nx-sieve__sort" role="group" aria-label="сигналы">
          <button
            type="button"
            className={`ico ${alarm.sound ? 'on' : ''}`}
            aria-pressed={alarm.sound}
            aria-label={TRANSFERS ? 'звук нового перелива' : 'звук новой вилки'}
            title={alarm.sound ? 'звук включён' : 'звук выключен'}
            onClick={() => alarm.setSound(!alarm.sound)}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 5 6 9H2v6h4l5 4z" />
              {alarm.sound ? (
                <>
                  <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                  <path d="M18.4 5.6a9 9 0 0 1 0 12.8" />
                </>
              ) : (
                <>
                  <line x1="22" y1="9" x2="16" y2="15" />
                  <line x1="16" y1="9" x2="22" y2="15" />
                </>
              )}
            </svg>
          </button>
          <button
            type="button"
            className={`ico ${alarm.notify ? 'on' : ''}`}
            aria-pressed={alarm.notify}
            aria-label={TRANSFERS ? 'уведомление браузера о новом переливе' : 'уведомление браузера о новой вилке'}
            title={
              alarm.denied
                ? 'браузер запретил уведомления для этого сайта, разрешение снимается в его настройках'
                : alarm.notify
                  ? 'уведомления включены (для вкладки во втором мониторе)'
                  : 'уведомления выключены'
            }
            onClick={() => void alarm.setNotify(!alarm.notify)}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              {(alarm.denied || !alarm.notify) && <line x1="3" y1="3" x2="21" y2="21" />}
            </svg>
          </button>
        </div>
        {/* Группы «режим» с кнопкой «мягкие» здесь больше нет (12.08, владелец:
            «бестолковый»). Она ставила границу процента 0.5–1.5 % одним нажатием
            рядом со звуком: способа работы за этим не стояло, а верх ленты от
            случайного нажатия пустел молча. Границу процента ставят в студии, где
            она стоит рядом со своими числами и держится профилем. */}
        <div className="nx-sieve__sort" role="group" aria-label="порядок">
          <span className="nx-sieve__grplbl">порядок</span>
          {fist ? (
            <select
              className="nx-sieve__order"
              aria-label="порядок"
              value={f.sort}
              onChange={(e) => f.setSort(e.target.value as SortKey)}
            >
              {(Object.keys(SORT_NAMES) as SortKey[]).map((key) => (
                <option key={key} value={key}>
                  {SORT_NAMES[key]}
                </option>
              ))}
            </select>
          ) : (
            (Object.keys(SORT_NAMES) as SortKey[]).map((key) => (
              <button
                key={key}
                type="button"
                className={f.sort === key ? 'on' : ''}
                aria-pressed={f.sort === key}
                onClick={() => f.setSort(key)}
              >
                {SORT_NAMES[key]}
              </button>
            ))
          )}
        </div>
      </div>

      {f.open && (
        <div
          className="nx-sieve__body"
          onKeyDown={(e) => {
            // Закрыть сито той же клавишей, которой закрывают всё остальное. Ловим
            // только на самой панели: общий обработчик ленты (`keys.ts`) снимает
            // этой же клавишей выделение строки, и перехватывать её у него, пока
            // человек смотрит в ленту, нельзя.
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            f.setOpen(false);
          }}
        >
          {/* Две дорожки вместо шести полос во всю ширину. Слева то, что задают
              числом (порог, банк, курс), справа то, что выбирают нажатием.
              Разделены чертой, а не рамками: панель и без того стоит в рамке, а
              рамка внутри рамки читается как второй уровень вложенности. */}
          {PRIVATE_MIRROR && <CupisPairs />}
          <div className="nx-sieve__lanes">
            <div className="nx-sieve__lane">
              {TRANSFERS ? <TransferCost /> : <Money />}
            </div>
            <div className="nx-sieve__lane">
              {/* Лайв и прематч на сканере — две страницы шапки, не фишка сита:
                  две ручки на один вопрос дают два ответа. Вне сканера (тесты,
                  макеты) переключатель остаётся: страница линии там не открыта.
                  Четвёртый пункт — полка, не фильтр линии: `lines` пишутся в
                  localStorage, а спрятанные живут до F5. Записать `hidden` в
                  lines — после перезагрузки опустошить ленту как мёртвый рынок. */}
              {locked === null && !isAllFeed(location.pathname) ? (
              <Cell label="лента">
                <Seg
                  label="лента"
                  value={lane}
                  options={[
                    { code: 'all' as const, name: 'вся' },
                    ...options.lines.map((one) => ({
                      code: one.code as 'live' | 'prematch' | 'hidden',
                      name: one.name,
                      count: one.count,
                    })),
                    { code: 'hidden' as const, name: 'скрытые', count: hiddenN },
                  ]}
                  onPick={(code) => {
                    if (code === 'hidden') {
                      setShelf('hidden');
                      return;
                    }
                    setShelf('work');
                    f.apply({ lines: code === 'all' ? [] : [code] });
                  }}
                />
              </Cell>
              ) : (
              <Cell label="лента">
                <Seg
                  label="лента"
                  value={shelf === 'hidden' ? 'hidden' : 'page'}
                  options={[
                    { code: 'page' as const, name: isAllFeed(location.pathname) ? 'все' : locked === 'live' ? 'лайв' : 'прематч' },
                    { code: 'hidden' as const, name: 'скрытые', count: hiddenN },
                  ]}
                  onPick={(code) => {
                    if (code === 'hidden') {
                      setShelf('hidden');
                      return;
                    }
                    setShelf('work');
                  }}
                />
              </Cell>
              )}
              <Cell label="дисциплины">
                <SportShelves
                  options={options.sports}
                  chosen={f.sports.map((one) => sportCanon(one)).filter(Boolean)}
                  onToggle={f.toggleSport}
                  onSet={(sports) => f.apply({ sports })}
                />
              </Cell>
              {RU_FEED && profile !== null && (
                <Cell label="точность">
                  <div className="nx-pick" role="group" aria-label="точность входа">
                    {([
                      ['coupon', 'купон'],
                      ['match', 'матч'],
                      ['site', 'сайт'],
                    ] as const).map(([code, name]) => (
                      <button
                        key={code}
                        type="button"
                        className={profile.prec === code ? 'on' : ''}
                        aria-pressed={profile.prec === code}
                        onClick={() =>
                          applyProfile({
                            ...profile,
                            prec: profile.prec === code ? 'any' : code,
                          })
                        }
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </Cell>
              )}
              {/* «На что ставим» — семейства, а не 81 написание рынка: человек,
                  работающий только тоталы, отсекает две трети ленты одним
                  нажатием. Замер 07.08: четыре семейства покрывают 98% строк. */}
              <Cell label="рынки">
                <div className="nx-cell__mode">
                  <Seg
                    label="как читать отметки рынков"
                    value={f.marketMode ?? 'except'}
                    options={[
                      { code: 'only' as const, name: 'только эти' },
                      { code: 'except' as const, name: 'все, кроме' },
                    ]}
                    onPick={f.setMarketMode}
                  />
                  <i>{marketHint(f)}</i>
                </div>
                {MARKET_GROUPS.map((group) => (
                  <div className="nx-sieve__grp" key={group.id}>
                    <span className="nx-sieve__grpname">{group.name}</span>
                    <ChipList
                      options={group.tags.map((tag) => ({ ...tag, count: options.markets.get(tag.code) ?? 0 }))}
                      chosen={f.markets}
                      onToggle={f.toggleMarket}
                    />
                  </div>
                ))}
              </Cell>
            </div>
          </div>
          {/* Конторы — единственная настройка во всю ширину, и она это место
              заслуживает: их семнадцать, и именно про них человек спрашивает
              «где у меня счёт». Полки по роду счёта — в `Books.tsx`.
              Счета — «только эти / все, кроме». Главная — отдельная полка
              выше: иначе одно чтение затирает другое. */}
          {sourceBooks}
          {PRIVATE_MIRROR && <CupisDiagnostics />}
          <div className="nx-sieve__foot">
            {!TRANSFERS && <Watch f={f} />}
            {/* Дверь в тонкую настройку названа прямо. Новичок иначе ищет в сите
                то, чего здесь нарочно нет, и решает, что этого нет в продукте.
                Стол — тоже: в фильтрах вилки его крутили как «только вход». */}
            {!PRIVATE_MIRROR && <p className="nx-sieve__deep">
              {TRANSFERS
                ? 'возраст строки и качество перелива живут '
                : 'проценты, возраст строки, стол и «мои конторы» живут '}
              <a href={studioHref('desk')}>в студии фильтров</a>. там настройки сохраняются
              профилем, а здесь остаётся то, что меняют на ходу.
            </p>}
          </div>
        </div>
      )}
    </div>
  );
}
